/* Doctrine inheritance made visible (UI-UX-DESIGN.md §5.3).
 *
 * Command PE layers Doctrine/ROE/WRA/EMCON across side → mission → group → unit. It is
 * powerful and it is a documented source of user confusion, because the effective value
 * is not visible at the point of use. Fix: state the effective value first, name its
 * source, and show whether every level inherits or overrides.
 */
import { useEffect, useState } from 'react'
import { CornerDownRight, Pencil } from 'lucide-react'
import type { DoctrineLevel } from '../data/types'
import { useDeck } from '../state/store'
import { fetchDoctrine } from '../lib/liveTraces'

function Chain({ title, levels }: { title: string; levels: DoctrineLevel[] }) {
  const source = levels.find((l) => l.isSource)

  return (
    <div
      className="mb-2 overflow-hidden rounded"
      style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-line-2)' }}
    >
      <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--color-line-1)' }}>
        <div className="label" style={{ fontSize: 9 }}>
          {title}
        </div>
        {/* Effective value first — this is what the operator actually needs */}
        <div className="mt-0.5 flex items-baseline gap-2">
          <span className="num" style={{ fontSize: 13.5, color: 'var(--color-warn)' }}>
            {source?.value ?? '—'}
          </span>
          <span style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
            from {source?.level}
            {source?.scope ? ` · ${source.scope}` : ''}
          </span>
        </div>
      </div>

      <div>
        {levels.map((l, i) => {
          const overrides = l.value !== 'inherit'
          return (
            <div
              key={l.level}
              className="flex items-center gap-2 px-3 py-1"
              style={{
                borderBottom:
                  i < levels.length - 1 ? '1px solid var(--color-line-1)' : undefined,
                background: l.isSource ? 'var(--color-accent-dim)' : undefined,
              }}
            >
              <span style={{ paddingLeft: i * 8 }}>
                {i > 0 && (
                  <CornerDownRight
                    size={10}
                    style={{ color: 'var(--color-text-3)' }}
                    aria-hidden
                  />
                )}
              </span>
              <span
                className="label"
                style={{ fontSize: 9, minWidth: 46, color: 'var(--color-text-3)' }}
              >
                {l.level}
              </span>
              <span
                className="min-w-0 flex-1 truncate"
                style={{ fontSize: 11, color: 'var(--color-text-2)' }}
              >
                {l.scope}
              </span>
              <span
                className="num shrink-0"
                style={{
                  fontSize: 10.5,
                  color: overrides ? 'var(--color-text-1)' : 'var(--color-text-3)',
                  fontStyle: overrides ? undefined : 'italic',
                }}
              >
                {l.value}
              </span>
            </div>
          )
        })}
      </div>

      <div className="flex gap-1 px-3 py-2">
        <button
          className="ctl flex-1"
          style={{ background: 'var(--color-surface-3)', fontSize: 11 }}
          title="Override at unit level"
        >
          <Pencil size={11} />
          Override at unit
        </button>
        <button
          className="ctl flex-1"
          style={{ background: 'var(--color-surface-3)', fontSize: 11 }}
          title="Override at group level"
        >
          <Pencil size={11} />
          At group
        </button>
      </div>
    </div>
  )
}

export function DoctrineChainPanel({ entityId }: { entityId: string | null }) {
  // Resolved by the ENGINE, including the level that supplied the effective value. The provenance
  // is the feature: "WEAPONS TIGHT" tells an operator they cannot shoot, while "WEAPONS TIGHT, set
  // at side level" tells them who to ask — and Command PE shows only the first.
  const live = useDeck((s) => s.connection === 'live')
  const [levels, setLevels] = useState<DoctrineLevel[]>([])

  useEffect(() => {
    if (!live || !entityId) {
      setLevels([])
      return
    }
    let cancelled = false
    void fetchDoctrine(entityId).then((rows) => {
      if (!cancelled) setLevels(rows)
    })
    return () => {
      cancelled = true
    }
  }, [live, entityId])

  if (!levels.length) {
    return (
      <div className="px-4 py-8 text-center">
        <p style={{ fontSize: 12, color: 'var(--color-text-2)' }}>
          {live
            ? 'No doctrine chain available for this unit.'
            : 'Not connected — doctrine is resolved by the engine.'}
        </p>
      </div>
    )
  }

  return (
    <div className="p-2">
      <Chain title="ROE — Air-to-Air engagement" levels={levels} />
      {/* One chain, not two. The panel used to render a second fabricated WRA chain beside the
          ROE one; the engine resolves a single posture chain and inventing a parallel one would
          show an inheritance that does not exist. Per-weapon WRA rules resolve separately and get
          their own panel when the engine exposes them. */}
    </div>
  )
}
